/**
 * Ledger categories: TC Fee, Old Dues, Electricity … (income / expense)
 */
import React, { useCallback, useEffect, useState } from "react";
import { Row, Col, Card, Table, Button, Form, Badge, Modal, Nav } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import schoolApi from "../../api/schoolApi";
import { inr, useCan } from "./ledgerUtils";

const empty = (type) => ({ name: "", type, default_amount: "", needs_student: false, status: "active" });

const LedgerCategories = ({ tabName }) => {
  const can = useCan("Ledger_Categories");
  const [tab, setTab] = useState("INCOME");
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null); // category being added / edited
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await schoolApi.ledgerGet("/categories", {});
      setRows(r?.result || []);
    } catch {
      toast.error("Could not load categories");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!edit.name.trim()) return toast.warn("Enter category name");
    if (edit.default_amount !== "" && Number(edit.default_amount) < 0) return toast.warn("Amount can not be negative");
    setSaving(true);
    try {
      const body = { ...edit, name: edit.name.trim(), default_amount: edit.default_amount === "" ? null : Number(edit.default_amount) };
      const r = edit.id
        ? await schoolApi.ledgerPut(`/categories/${edit.id}`, body)
        : await schoolApi.ledgerPost("/categories", body);
      if (r?.success) {
        toast.success("Category saved");
        setEdit(null);
        load();
      } else toast.error(r?.message || "Could not save");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (c) => {
    try {
      const r = await schoolApi.ledgerPut(`/categories/${c.id}`, { status: c.status === "active" ? "inactive" : "active" });
      if (r?.success) load();
      else toast.error(r?.message || "Could not update");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not update");
    }
  };

  const list = rows.filter((r) => r.type === tab);

  return (
    <>
      <Helmet><title>{tabName || "Ledger Categories"}</title></Helmet>
      <Row className="g-0 px-lg-4 px-2">
        <Col lg={12} className="d-flex justify-content-between align-items-center mt-3 mb-2">
          <Link className="nav-link" to="/">
            Home <i className="fa-solid fa-chevron-right" /> Accounts <i className="fa-solid fa-chevron-right" /> Categories
          </Link>
          {can.create && (
            <Button onClick={() => setEdit(empty(tab))}>
              <i className="fa-solid fa-plus me-1" /> Add {tab === "INCOME" ? "Income" : "Expense"} Category
            </Button>
          )}
        </Col>
        <Col lg={12}>
          <Card className="shadow-sm">
            <Card.Header className="bg-white">
              <Nav variant="tabs" activeKey={tab} onSelect={(k) => setTab(k)}>
                <Nav.Item><Nav.Link eventKey="INCOME">Income</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link eventKey="EXPENSE">Expense</Nav.Link></Nav.Item>
              </Nav>
            </Card.Header>
            <Card.Body className="p-0">
              <Table hover responsive className="mb-0 align-middle" size="sm">
                <thead style={{ background: "#637384", color: "#fff" }}>
                  <tr>
                    <th className="ps-3">Name</th>
                    <th className="text-end">Default Amount</th>
                    <th>Needs Student</th>
                    <th className="text-end">Active Entries</th>
                    <th>Status</th>
                    <th className="text-end pe-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {list.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-muted py-4">No category yet</td></tr>
                  )}
                  {list.map((c) => (
                    <tr key={c.id}>
                      <td className="ps-3 fw-semibold">{c.name}</td>
                      <td className="text-end">{c.default_amount != null ? `₹ ${inr(c.default_amount)}` : "–"}</td>
                      <td>{c.needs_student ? <Badge bg="info">Yes</Badge> : "No"}</td>
                      <td className="text-end">{c.entry_count}</td>
                      <td>
                        <Badge bg={c.status === "active" ? "success" : "secondary"}>{c.status}</Badge>
                      </td>
                      <td className="text-end pe-3 text-nowrap">
                        {can.edit && (
                          <>
                            <Button size="sm" variant="outline-primary" onClick={() => setEdit({ ...c, default_amount: c.default_amount ?? "" })}>
                              <i className="fa-solid fa-pen" />
                            </Button>{" "}
                            <Button size="sm" variant={c.status === "active" ? "outline-secondary" : "outline-success"} onClick={() => toggleStatus(c)}>
                              {c.status === "active" ? "Deactivate" : "Activate"}
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={!!edit} onHide={() => setEdit(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 17 }}>
            {edit?.id ? "Edit" : "Add"} {edit?.type === "EXPENSE" ? "Expense" : "Income"} Category
          </Modal.Title>
        </Modal.Header>
        {edit && (
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">Name *</Form.Label>
              <Form.Control maxLength={100} value={edit.name} autoFocus
                placeholder={edit.type === "INCOME" ? "e.g. TC Fee" : "e.g. Electricity"}
                onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">Default Amount (₹)</Form.Label>
              <Form.Control type="number" min="0" value={edit.default_amount}
                onKeyDown={(e) => ["-", "e", "E", "+"].includes(e.key) && e.preventDefault()}
                onChange={(e) => setEdit({ ...edit, default_amount: e.target.value })}
                placeholder="Filled in automatically, can be changed" />
            </Form.Group>
            <Form.Check type="switch" id="needs_student" label="Always needs a student (e.g. TC Fee)"
              checked={!!edit.needs_student} onChange={(e) => setEdit({ ...edit, needs_student: e.target.checked })} />
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="light" onClick={() => setEdit(null)}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LedgerCategories;
